import { createRouter, createWebHistory } from 'vue-router'
import Dashboard from '@/views/Dashboard.vue'
import authService from '@/utils/auth.js'

const routes = [
  {
    path: '/',
    name: 'home',
    meta: {
      requiresAuth: true
    },
    component: Dashboard
  },
  {
    path: '/login',
    name: 'login',
    meta: {
      title: 'Log in | BookedSolid',
      description: 'Log in to your BookedSolid dashboard to manage calls, bookings and reminders for your clinic.',
      canonical: 'https://dashboard.bookedsolid.health/login'
    },
    component: () => import(/* webpackChunkName: "adminLogin" */ '../views/LoginView.vue'),
    beforeEnter: to => {
      if (to.query.show !== 'register' && !to.query.referrer) return true;
      const { show, ...query } = to.query;
      return { name: 'register', query, replace: true };
    }
  },
  {
    path: '/register',
    name: 'register',
    meta: {
      noindex: true
    },
    component: () => import(/* webpackChunkName: "adminRegister" */ '../views/RegisterView.vue')
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminOnboarding" */ '../views/OnboardingView.vue')
  },
  {
    path: '/messages',
    name: 'messages',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: "adminMessages" */ '../views/MessagesView.vue')
  },
  {
    path: '/calls',
    name: 'calls',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: "adminCalls" */ '../views/CallsView.vue')
  },
  {
    path: '/public-voice-demo',
    name: 'public-voice-demo',
    component: () => import(/* webpackChunkName: "publicVoiceDemo" */ '../components/PublicVoiceDemo.vue')
  },
  {
    path: '/outbound',
    name: 'outbound',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminOutbound" */ '../views/OutboundView.vue')
  },
  {
    path: '/channels',
    name: 'channels',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminChannels" */ '../views/ChannelsView.vue')
  },
  {
    path: '/clinic',
    name: 'clinic',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminClinic" */ '../views/ClinicSetupView.vue')
  },
  {
    path: '/isv',
    name: 'isv',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminInfo" */ '../views/ISVView.vue')
  },
  {
    path: '/scripting',
    name: 'scripting',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminScripting" */ '../views/ScriptView.vue')
  },
  {
    path: '/settings',
    name: 'settings',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "adminSettings" */ '../views/SettingsView.vue')
  },
  {
    path: '/reset_user_password/:id',
    name: 'password',
    component: () => import(/* webpackChunkName: "adminPasswordReset" */ '../views/PasswordView.vue')
  },
  {
    path: '/referral',
    name: 'referral',
    component: () => import(/* webpackChunkName: "referral" */ '../views/ReferralView.vue')
  },
  {
    path: '/connect-stripe',
    name: 'connect-stripe',
    component: () => import(/* webpackChunkName: "adminConnectStripe" */ '../views/StripeOauthView.vue')
  },
  {
    path: '/audio-player',
    name: 'audio-player',
    component: () => import(/* webpackChunkName: "adminAudioPlayer" */ '../views/AudioPlayerView.vue'),
  },

  {
    path: '/demo',
    name: 'demo',
    component: () => import(/* webpackChunkName: "publicVoiceDemo" */ '../views/DemoView.vue')
  },

  // Admin routes

  {
    path: '/admin/',
    name: 'admin-tracking',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "admin-tracking" */ '../views/admin/TrackingView.vue')
  },
  {
    path: '/admin/data-harvest',
    name: 'data-harvest',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "data-harvest" */ '../views/admin/DataHarvestView.vue')
  },
  {
    path: '/admin/manage-clients',
    name: 'manage-clients',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "manage-clients" */ '../views/admin/ManageClientsView.vue')
  },
  {
    path: '/admin/request-log',
    name: 'request-log',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "request-log" */ '../views/admin/RequestLogView.vue')
  },
  {
    path: '/admin/helper-chats',
    name: 'helper-chats',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "helper-chats" */ '../views/admin/HelperChatsView.vue')
  },
  {
    path: '/admin/integration-tester',
    name: 'integration-tester',
    meta: {
      requiresAdminAuth: true
    },
    component: () => import(/* webpackChunkName: "integration-tester" */ '../views/admin/IntegrationTesterView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // always scroll to top
    return { top: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  // Routes that don't require authentication
  const publicRoutes = ['login', 'register', 'password', 'reset_user_password', 'connect-stripe', 'audio-player', 'referral'];
  
  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth || record.meta.requiresAdminAuth);
  
  if (!requiresAuth || publicRoutes.includes(to.name)) {
    // Public route or no auth required
    next();
    return;
  }

  // If this is a loginAsClient URL (token + client_id in query), skip auth validation —
  // App.vue mounted() will call activateToken() to exchange the JWT for a session cookie
  // before reloading, so validating now would fail (the cookie isn't set yet).
  if (to.query.token && to.query.client_id) {
    next();
    return;
  }

  // Check if we have a token before making network requests
  if (!authService.hasToken()) {
    next({ name: 'login', query: { error: 'no_token', redirect: to.fullPath } });
    return;
  }

  // Show loading state for auth check
  const loadingEl = document.getElementById('app-loading');
  if (loadingEl) {
    loadingEl.classList.add('show');
    loadingEl.style.display = 'flex';
    
    // Hide navigation elements
    const desktopNav = document.getElementById('adminDesktopNav');
    const mobileNav = document.getElementById('adminMobileNav');
    const routerView = document.querySelector('.router-view');
    
    if (desktopNav) desktopNav.style.display = 'none';
    if (mobileNav) mobileNav.style.display = 'none';
    if (routerView) routerView.style.display = 'none';
  }

  try {
    // Add retry logic for Safari compatibility
    let authResult;
    let retryCount = 0;
    const maxRetries = 2;
    
    while (retryCount <= maxRetries) {
      try {
        // Use cached validation if available and recent (within 30 seconds for navigation)
        const cacheValid = authService.isValidationCacheValid() && 
                          (Date.now() - authService.lastValidationTime) < 30000;
        
        authResult = await authService.validateAuth(!cacheValid);
        break; // Success, exit retry loop
      } catch (error) {
        retryCount++;
        
        // If it's a network error and we have retries left, wait and retry
        if (retryCount <= maxRetries && 
            (error.code === 'ECONNABORTED' || error.message?.includes('Network Error'))) {
          console.warn(`Auth validation attempt ${retryCount} failed, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
          continue;
        } else {
          throw error; // Re-throw if not retryable or out of retries
        }
      }
    }
    
    if (authResult && authResult.isValid) {
      // Check admin requirement using server-validated data
      if (to.matched.some(record => record.meta.requiresAdminAuth)) {
        const user = authResult.user;
        const isValidAdmin = user && (user.isAdmin || user.security_level >= 5);
        
        if (!isValidAdmin) {
          console.warn('Access denied: Insufficient privileges');
          next({ name: 'login', query: { error: 'insufficient_privileges', redirect: to.fullPath } });
          return;
        }
      }
      
      // Authentication successful
      next();
    } else {
      // Authentication failed - but be more lenient with network errors
      console.warn('Authentication failed:', authResult?.error);
      
      // Handle different types of auth failures
      let errorQuery = { redirect: to.fullPath };
      
      if (authResult?.expired) {
        errorQuery.error = 'session_expired';
      } else if (authResult?.forbidden) {
        errorQuery.error = 'access_forbidden';
      } else if (authResult?.timeout) {
        errorQuery.error = 'validation_timeout';
      } else if (authResult?.networkError) {
        errorQuery.error = 'network_error';
      } else {
        errorQuery.error = 'invalid_session';
      }
      
      next({ name: 'login', query: errorQuery });
    }
  } catch (error) {
    console.error('Router auth check error:', error);
    
    next({ 
      name: 'login', 
      query: { 
        error: 'auth_check_failed', 
        redirect: to.fullPath 
      } 
    });
  } finally {
    // Hide loading state and restore navigation
    const loadingEl = document.getElementById('app-loading');
    if (loadingEl) {
      loadingEl.classList.remove('show');
      loadingEl.style.display = 'none';
      
      // Restore navigation elements
      const desktopNav = document.getElementById('adminDesktopNav');
      const mobileNav = document.getElementById('adminMobileNav');
      const routerView = document.querySelector('.router-view');
      
      if (desktopNav) desktopNav.style.display = '';
      if (mobileNav) mobileNav.style.display = '';
      if (routerView) routerView.style.display = '';
    }
  }
})

export default router
